import { useState } from 'react';
import { useAPI } from 'services/useAPI';
import { useFetchData, useUpdateData } from 'services/queries';
import toast from 'react-hot-toast';

/**
 * Custom hook for handling tax calculation workflow
 * Manages prerequisites check and tax calculation for orders
 */
export const useTaxCalculation = () => {
    const api = useAPI();
    const [checkingOrderId, setCheckingOrderId] = useState(null);
    const [prerequisitesData, setPrerequisitesData] = useState(null);
    const [prerequisitesDialogOpen, setPrerequisitesDialogOpen] = useState(false);

    // Fetch existing tax calculations
    const { 
        data: taxCalculations, 
        isLoading: isLoadingTaxCalcs,
        refetch: refetchTaxCalculations 
    } = useFetchData('tax/order-tax-calculation', true);

    // Mutation for calculating taxes
    const calculateTaxMutation = useUpdateData('tax/order-tax-calculation/calculate');

    // Check if an order has tax calculation
    const hasOrderTaxCalculation = (orderId) => {
        if (!taxCalculations) return false;
        return taxCalculations.some(calc => calc.sales_order === orderId);
    };

    // Handle tax calculation with prerequisites check
    const handleCalculateTaxes = async (orderId, options = {}) => {
        setCheckingOrderId(orderId);
        
        try {
            // First, check prerequisites
            const prerequisitesResponse = await api.get(`tax/prerequisites/${orderId}/`);
            const prerequisites = prerequisitesResponse.data;

            if (prerequisites.can_calculate) {
                // Show warnings if any (calculation will proceed)
                if (prerequisites.warnings && prerequisites.warnings.length > 0) {
                    prerequisites.warnings.forEach(w => toast(w.message, { icon: '⚠️' }));
                }
                // Prerequisites met, proceed with calculation
                calculateTaxMutation.mutate(
                    { id: orderId },
                    {
                        onSuccess: () => {
                            toast.success('Tax calculation completed successfully');
                            refetchTaxCalculations();
                            setCheckingOrderId(null);
                            if (options.onSuccess) {
                                options.onSuccess();
                            }
                        },
                        onError: (error) => {
                            toast.error(error?.response?.data?.message || 'Failed to calculate taxes');
                            setCheckingOrderId(null);
                            if (options.onError) {
                                options.onError(error);
                            }
                        },
                        onSettled: () => {
                            // Always reset checking state
                            setCheckingOrderId(null);
                            if (options.onSettled) {
                                options.onSettled();
                            }
                        }
                    }
                );
            } else {
                // Prerequisites not met, show dialog
                setPrerequisitesData(prerequisites);
                setPrerequisitesDialogOpen(true);
                setCheckingOrderId(null);
                
                if (options.onPrerequisitesFailed) {
                    options.onPrerequisitesFailed(prerequisites);
                }
            }
        } catch (error) {
            console.error('Error checking prerequisites:', error);
            toast.error('Failed to check tax prerequisites');
            setCheckingOrderId(null);
            
            if (options.onError) {
                options.onError(error);
            }
        }
    };

    // Close prerequisites dialog
    const closePrerequisitesDialog = () => {
        setPrerequisitesDialogOpen(false);
        setPrerequisitesData(null);
    };

    return {
        // States
        checkingOrderId,
        prerequisitesData,
        prerequisitesDialogOpen,
        isLoadingTaxCalcs,
        taxCalculations,
        
        // Functions
        handleCalculateTaxes,
        hasOrderTaxCalculation,
        closePrerequisitesDialog,
        refetchTaxCalculations
    };
};

export default useTaxCalculation;